/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useRef } from 'react';

import { IconArrowBack } from '@genesis/icons';
import { Avatar, Field, Input, Label } from '@genesis/ui';
import { useForm, useStore } from '@tanstack/react-form';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';

import { QueryParams, queryParams } from '@/app/constants/queryParams';
import { episodesDetailsQueryOptions } from '@/app/queries/episodes/episodesDetails';
import { photoService } from '@/app/services/photoService';
import { sqids } from '@/app/utils/hashids/sqids-generator';
import { createImageMetadata } from '@/app/utils/metadata/create-image-metadata';
import { time } from '@/app/utils/time';
import { Button } from '@/common/components/elements/button';
import { TextArea } from '@/common/components/elements/textarea';
import { feedbackEvent } from '@/common/components/features/feedback/feedback-event';
import { PhotoUpload } from '@/common/components/features/photo-upload/photo-upload';
import { Can, useAbility } from '@/common/components/features/role-management';
import { Badge } from '@/common/components/modules/badge';
import { AudioProvider } from '@/common/contexts/audio-context';
import { useImageUpload } from '@/common/hooks/upload/use-image-upload';
import { useAnalyticsEvent } from '@/common/hooks/use-analytics-events';
import { useAuth } from '@/common/hooks/use-auth';
import { useBlockNavigation } from '@/common/hooks/use-block-navigation';
import { captureError } from '@/infra/exceptions/helpers/captureError';
import { defaultMessage } from '@/infra/exceptions/helpers/defaultMessage';
import { FeaturesRequest } from '@/infra/observability/sentry';
import { dateUtils } from '@/infra/protocols/date';
import { useGetAudioQuery } from '@/modules/audio/queries/use-get-audio-query';
import { EpisodeStatus } from '@/modules/episodes/enums/episode-status';
import { useUpdateEpisodeMutation } from '@/modules/episodes/mutations/use-update-episode-mutation';
import { episodesService } from '@/modules/episodes/services';
import { usePublishEpisodeMutation } from '@/modules/publish/mutations/use-publish-episode-mutation';
import { publishSchema } from '@/modules/publish/schemas/publish-schema';
import { AudioInformation } from '@/modules/studio/components/audio-information';
import { AudioPlayer } from '@/modules/studio/components/audio-player';

function Publish() {
  const { user } = useAuth();

  const { id: studioIds } = Route.useParams();
  const { episodeId, podcastId, projectId } = sqids.hydrate<QueryParams.Studio>(
    studioIds,
    queryParams.studio,
  );

  const { data: details } = useSuspenseQuery(
    episodesDetailsQueryOptions(projectId, podcastId, episodeId),
  );

  const { data: audioUrl, isLoading: isLoadingAudioUrl } = useGetAudioQuery({
    projectId,
    podcastId,
    episodeId,
  });

  const { mutateAsync: updateEpisodeMutation } = useUpdateEpisodeMutation(projectId, podcastId);

  const { mutateAsync: publishEpisodeMutation } = usePublishEpisodeMutation(
    projectId,
    podcastId,
    episodeId,
  );

  const analytics = useAnalyticsEvent({ projectId, podcastId, episodeId });

  const ability = useAbility();

  const inputPhotoFileRef = useRef<HTMLInputElement>(null);

  const photo = photoService.getDefaultProfilePhoto(details?.author ?? '');

  const isNotEnabledToEdit = useMemo(() => ability.cannot('update', 'Episode'), [ability]);

  const disabledPublish = useMemo(
    () =>
      details.status === EpisodeStatus.Publicado ||
      details.status === EpisodeStatus.Publicando ||
      !audioUrl?.url ||
      isNotEnabledToEdit,
    [audioUrl?.url, details.status, isNotEnabledToEdit],
  );

  const photoUpload = useImageUpload({
    maxFiles: 1,
    multiple: false,
    generatePreview: true,
    accept: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  });

  const form = useForm({
    defaultValues: {
      name: details.title,
      description: details.description,
    },
    validators: { onSubmit: publishSchema },
    onSubmit: async ({ value, formApi }) => {
      const { name, description } = value;

      const id = crypto.randomUUID();

      const selectedFile = photoUpload.files[0];
      const selectedFileName = selectedFile?.file.name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w\s\-.,!?'"]/g, '');

      try {
        const { id: updatedId, presignedUrl } = await updateEpisodeMutation({
          data: {
            name,
            description,
            fileName: `${id}_${selectedFileName}`,
            imageUrl: selectedFile?.previewUrl,
          },
          params: {
            podcastId,
            projectId,
            episodeId: Number(episodeId),
          },
        });

        if (selectedFile?.file && presignedUrl) {
          const metadata = createImageMetadata('atribuir_imagem_episodio', {
            idUsuario: user!.id,
            idPodcast: podcastId,
            idProjeto: projectId,
            idEpisodio: episodeId.toString(),
          });

          const { error } = await photoUpload.uploadFile(selectedFile.file, presignedUrl, metadata);

          if (error) {
            toast.error(photoUpload.getErrorMessage(error));
            return;
          }

          // Verificar nome do arquivo retornado pelo backend para garantir que a foto esteja atualizada antes de publicar
          let isUpdated = false;
          let requestTimes = 0;

          while (isUpdated === false && requestTimes < 3) {
            requestTimes++;

            const response = await episodesService.getEpisodeDetails({
              episodeId,
              podcastId,
              projectId,
            });

            if (response.thumbName) {
              isUpdated = selectedFile?.file.name === response.thumbName;
            }
          }
        }

        if (updatedId) {
          const response = await publishEpisodeMutation({
            data: {
              title: name,
              description,
              imageUrl: selectedFile?.previewUrl,
            },
            params: {
              podcastId,
              projectId,
              episodeId: Number(episodeId),
            },
          });

          toast.success(
            `O episódio está sendo enviado para as plataformas configuradas. Status: ${response.status}.`,
          );

          analytics.send(
            {
              label: analytics.events.main.publish_episode,
              name: analytics.events.main.publish_episode,
            },
            ['SUCCESSFUL', 'MAIN'],
          );
        }

        formApi.reset();
        photoUpload.clear();

        feedbackEvent('publish_episode');
      } catch (error) {
        analytics.send(
          {
            label: analytics.events.main.publish_episode,
            name: analytics.events.main.publish_episode,
          },
          ['ERROR', 'MAIN'],
        );

        toast.error(
          captureError(
            error,
            {
              feature: FeaturesRequest.PUBLISH_EPISODE,
              defaultMessage: defaultMessage('publicar o episódio'),
            },
            {
              projectId,
              podcastId,
            },
          ).message,
        );
      }
    },
  });

  const isDirty = useStore(form.store, ({ isDirty }) => isDirty);

  const hasImageChanges = photoUpload.files.length > 0;

  const [isBlocked] = useBlockNavigation(isDirty || hasImageChanges);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.handleSubmit();
  };

  return (
    <AudioProvider>
      <div className='flex bg-surface-neutral-secondary flex-1 overflow-hidden'>
        <div className='flex flex-col h-full w-full'>
          <div className='grid grid-cols-2 w-full bg-surface-neutral-primary border-b border-border-neutral h-12 px-3 py-2'>
            <div className='col-span-1 overflow-hidden'>
              <Link
                to='/studio/$id'
                className='flex items-center gap-2 w-fit h-full'
                params={{ id: studioIds }}
              >
                <IconArrowBack className='size-4 fill-content-neutral-primary shrink-0' />
                <span className='truncate text-sm font-medium flex-1'>Voltar</span>
              </Link>
            </div>
          </div>

          <div className='flex flex-col mb:flex-row flex-1 p-4 gap-4 overflow-scroll'>
            <div className='bg-surface-neutral-primary border rounded-lg flex flex-col flex-1 min-h-fit'>
              <div className='flex flex-col p-4 border-b gap-2 pb-6'>
                <h2 className='text-xl font-medium'>
                  Episódio #{details.number} - {details.podcastName}
                </h2>

                <div className='flex gap-2 items-center'>
                  <Badge size='lg'>{details.status}</Badge>

                  <div className='flex items-center gap-2'>
                    <span className='text-sm'>Autor:</span>
                    <Avatar src={photo} size='small' />
                    <p className='text-base capitalize'>
                      {details?.author?.split('@')[0]?.replaceAll('.', ' ')}
                    </p>
                  </div>
                </div>
              </div>

              <div className='flex flex-col p-4 pt-6 gap-2'>
                <h2 className='text-xl font-medium'>Informações da publicação</h2>

                <form
                  aria-busy={isBlocked}
                  className='flex flex-col gap-7 w-full p-4 lg:flex-row'
                  onSubmit={handleSubmit}
                >
                  <div className='flex justify-center'>
                    <PhotoUpload
                      size='medium'
                      ref={inputPhotoFileRef}
                      src={details?.thumbnail}
                      alt='Preview do episódio'
                      photoUpload={photoUpload}
                      disabled={disabledPublish}
                      defaultSrc='/images/episode.webp'
                      onSelect={() => inputPhotoFileRef.current?.click()}
                    />
                  </div>

                  <div className='flex flex-1 flex-col gap-7 w-full lg:flex-row'>
                    <div className='flex flex-col flex-1 gap-3'>
                      <form.Field name='name'>
                        {(field) => (
                          <Field className='flex flex-col w-full'>
                            <Label className='text-content-neutral-secondary text-sm/normal font-medium block'>
                              Nome
                            </Label>
                            <Input
                              className='w-full rounded-md'
                              value={field.state.value}
                              onChange={(event) => {
                                field.handleChange(event.target.value);
                              }}
                              invalid={field.state.meta.errors.length > 0}
                              disabled={disabledPublish}
                            />
                            <span className='text-content-critical text-sm'>
                              {field.state.meta.errors.length > 0 &&
                                field.state.meta.errors[0]?.message}
                            </span>
                          </Field>
                        )}
                      </form.Field>

                      <form.Field name='description'>
                        {(field) => (
                          <TextArea
                            fullWidth
                            label='Descrição'
                            value={field.state.value}
                            onChange={field.handleChange}
                            error={field.state.meta.errors[0]?.message}
                            disabled={disabledPublish}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <div className='bg-surface-neutral-primary border rounded-lg flex flex-col min-w-1/4'>
              <div className='flex flex-col p-4 border-b gap-2'>
                <p className='text-lg font-medium'>Áudio</p>

                <div className='flex flex-col gap-1'>
                  <form.Subscribe selector={(state) => state.values.name}>
                    {(name) => (
                      <AudioInformation audioUrl={audioUrl?.url} name={name}>
                        {({ mime, size, duration }) => (
                          <>
                            <p className='text-sm'>Duração: {time.formatBySeconds(duration)}</p>
                            <p className='text-sm'>Tamanho: {size}</p>
                            <p className='text-sm'>Tipo: {mime}</p>
                          </>
                        )}
                      </AudioInformation>
                    )}
                  </form.Subscribe>
                </div>
              </div>

              <div className='flex flex-col p-4 border-b gap-2'>
                <p className='text-lg font-medium'>Detalhes</p>

                <div className='flex flex-col gap-1'>
                  <p className='text-sm'>
                    Data de criação:{' '}
                    {details.createdAt ? dateUtils.formatDate(details.createdAt) : '--'}
                  </p>
                  <p className='text-sm'>
                    Ultima edição:{' '}
                    {details.updatedAt ? dateUtils.formatDate(details.updatedAt) : '--'}
                  </p>
                  <p className='text-sm'>
                    Data de publicação:{' '}
                    {details.endedAt ? dateUtils.formatDate(details.endedAt) : '--'}
                  </p>
                </div>
              </div>

              <div className='flex flex-col p-4 gap-2'>
                <p className='text-lg font-medium'>Ações</p>
                <div className='flex flex-col gap-2'>
                  <Can I='update' an='Episode'>
                    <form.Subscribe
                      selector={({ canSubmit, isSubmitting }) => [canSubmit, isSubmitting]}
                    >
                      {([canSubmit, isSubmitting]) => (
                        <Button
                          variant='primary'
                          className='w-full'
                          onClick={() => form.handleSubmit()}
                          disabled={!canSubmit || isSubmitting || disabledPublish}
                        >
                          Publicar
                        </Button>
                      )}
                    </form.Subscribe>
                  </Can>

                  <form.Subscribe selector={(state) => state.values.name}>
                    {(name) => (
                      <AudioInformation audioUrl={audioUrl?.url} name={name}>
                        {({ onDownload, url }) => (
                          <Button
                            variant='secondary'
                            onClick={onDownload}
                            className='w-full'
                            disabled={!url}
                          >
                            Baixar
                          </Button>
                        )}
                      </AudioInformation>
                    )}
                  </form.Subscribe>
                </div>
              </div>
            </div>
          </div>

          <div className='flex flex-col w-full border-t border-border-neutral bg-background-primary'>
            <div className='flex w-full h-14 items-center px-4 py-1'>
              <AudioPlayer
                podcastId={podcastId}
                projectId={projectId}
                episodeId={episodeId}
                audioUrl={audioUrl?.url}
                loading={isLoadingAudioUrl}
              />
            </div>
          </div>
        </div>
      </div>
    </AudioProvider>
  );
}

export const Route = createFileRoute('/_app/studio/$id/publicar')({
  component: Publish,
});
